import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useMemo } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { _ as _taggedTemplateLiteral } from '../../unsorted/taggedTemplateLiteral.js';
import { keyframes } from '@emotion/react';
import { jsx } from '@emotion/react/jsx-runtime';
import { B as Box } from '../Box/Box.js';
import { useTheme } from '../../hooks/useTheme.js';
import { B as Body } from '../Body/Body.js';

var _templateObject, _templateObject2;
var dotPulseAnimation = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  0% {\n    scale: 1;\n    opacity: 0.8;  \n  }\n  100% {\n    scale: 2.8;\n    opacity: 0\n  }\n"])));
var contentPulseAnimation = keyframes(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  0% {\n    scale: 1;\n    opacity: 0.8;  \n  }\n  100% {\n    scale: 1.5 1.9;\n    opacity: 0\n  }\n"])));
function getBadgeAnimationSx(isAnimated, hasContent) {
  return isAnimated ? {
    "&:before": {
      animation: "".concat(hasContent ? contentPulseAnimation : dotPulseAnimation, " infinite"),
      animationDuration: "1.3s",
      animationTimingFunction: "base.motion.normal.gentle.cssEase"
    }
  } : {};
}
var baseBadgeSx = {
  pos: "relative",
  flexShrink: "0",
  d: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "base.spacing.x2",
  py: "2px",
  brad: "base.spacing.x6",
  whiteSpace: "nowrap",
  "&:before": {
    content: "''",
    pos: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    brad: "base.spacing.x6",
    scale: "1"
  }
};
var plainDotBadgeSx = {
  p: "0px",
  w: "base.spacing.x2",
  h: "base.spacing.x2"
};
var getBadgeVariantSx = function getBadgeVariantSx(variant) {
  switch (true) {
    case variant === "emphasis":
      return {
        bg: "base.gradient.2",
        "&:before": {
          bg: "base.gradient.2"
        }
      };
    case variant === "dark":
      return {
        bg: "base.color.fixed.black.1000",
        "&:before": {
          bg: "base.color.fixed.black.1000"
        }
      };
    case variant === "light":
      return {
        bg: "base.color.fixed.white.1000",
        "&:before": {
          bg: "base.color.fixed.white.1000"
        }
      };
    case /guidance|success|attention|fatal/.test(variant):
    default:
      return {
        bg: "base.color.status.".concat(variant, ".bright"),
        "&:before": {
          bg: "base.color.status.".concat(variant, ".bright")
        }
      };
  }
};
var textSx = {
  pos: "relative"
};

var _excluded = ["rc", "domRef", "testId", "sx", "variant", "isAnimated", "badgeContent", "maxNumber", "showZero", "className"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Badge(_ref) {
  var _theme$components2;
  var _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    domRef = _ref.domRef,
    testId = _ref.testId,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "fatal" : _ref$variant,
    _ref$isAnimated = _ref.isAnimated,
    isAnimated = _ref$isAnimated === void 0 ? false : _ref$isAnimated,
    badgeContent = _ref.badgeContent,
    _ref$maxNumber = _ref.maxNumber,
    maxNumber = _ref$maxNumber === void 0 ? 999 : _ref$maxNumber,
    showZero = _ref.showZero,
    className = _ref.className,
    badgeDomAttributes = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var displayContent = useMemo(function () {
    if (typeof badgeContent === "number") {
      if (typeof maxNumber === "number" && badgeContent > maxNumber) {
        return "+".concat(maxNumber);
      }
      if (badgeContent === 0 && showZero) {
        return "".concat(badgeContent);
      }
      if (badgeContent === 0 && !showZero) {
        return null;
      }
    }
    return badgeContent;
  }, [badgeContent, maxNumber, showZero]);
  var hasContent = useMemo(function () {
    return Boolean(displayContent);
  }, [displayContent]);
  var badgeSx = useMemo(function () {
    var _theme$components$Bad, _theme$components;
    return merge(baseBadgeSx, getBadgeVariantSx(variant), getBadgeAnimationSx(isAnimated, hasContent), _objectSpread({}, !displayContent ? plainDotBadgeSx : {}), (_theme$components$Bad = (_theme$components = theme.components) === null || _theme$components === void 0 || (_theme$components = _theme$components.Badge) === null || _theme$components === void 0 ? void 0 : _theme$components.sxOverride) !== null && _theme$components$Bad !== void 0 ? _theme$components$Bad : {}, sx);
  }, [variant, isAnimated, hasContent, displayContent, (_theme$components2 = theme.components) === null || _theme$components2 === void 0 || (_theme$components2 = _theme$components2.Badge) === null || _theme$components2 === void 0 ? void 0 : _theme$components2.sxOverride, sx]);
  var invertText = useMemo(function () {
    return theme.base.colorMode === "onLight" && variant === "fatal" || variant === "dark";
  }, [theme.base.colorMode, variant]);
  var mergedTextSx = useMemo(function () {
    return merge(textSx, {
      c: invertText ? "base.color.fixed.white.1000" : "base.color.fixed.black.1000"
    });
  }, [invertText]);
  return jsx(Box, _objectSpread(_objectSpread({}, badgeDomAttributes), {}, {
    rc: rc,
    sx: badgeSx,
    domRef: domRef,
    testId: testId,
    className: "".concat(className !== null && className !== void 0 ? className : "", " Badge Badge--").concat(variant, " ").concat(invertText ? "Badge--inverted" : ""),
    children: Boolean(displayContent) && jsx(Body, {
      sx: mergedTextSx,
      weight: "bold",
      size: "xxSmall",
      testId: "".concat(testId, "__text"),
      className: "Badge__text",
      children: displayContent
    })
  }));
}
Badge.displayName = "Badge";

export { Badge as B };
//# sourceMappingURL=Badge.js.map
